import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import PublicHeader from "../../components/layout/PublicHeader";
import PublicFooter from "../../components/layout/PublicFooter";
import {
  Check, ChevronRight, Target, Zap, Timer,
  BarChart3, Trophy, RefreshCw, ArrowDown,
  Rocket, Medal, Flag
} from "lucide-react";

import SEO from "../../components/SEO";

const SimulationsPage = () => {
  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const easyTests = Array.from({ length: 10 }, (_, i) => ({
    number: i + 1,
    name: `Easy ${i + 1}`,
  }));

  const mediumTests = Array.from({ length: 10 }, (_, i) => ({
    number: i + 1,
    name: `Medium ${i + 1}`,
  }));

  const hardTests = Array.from({ length: 10 }, (_, i) => ({
    number: i + 1,
    name: `Hard ${i + 1}`,
  }));

  const journeySteps = [
    {
      phase: 1,
      title: "Foundation Phase",
      subtitle: "Easy Simulations 1-10",
      color: "bg-green-500",
      borderColor: "border-green-500",
      textColor: "text-green-600",
      bgColor: "bg-green-50",
      icon: <Target className="w-6 h-6" />,
      description: "Build your confidence with foundational questions. Master the basics before moving forward.",
      tests: easyTests,
      goal: "Score 75%+ on all 10 tests"
    },
    {
      phase: 2,
      title: "Development Phase",
      subtitle: "Medium Simulations 1-10",
      color: "bg-yellow-500",
      borderColor: "border-yellow-500",
      textColor: "text-yellow-600",
      bgColor: "bg-yellow-50",
      icon: <Zap className="w-6 h-6" />,
      description: "Challenge yourself with realistic test-level questions. This is where real learning happens.",
      tests: mediumTests,
      goal: "Score 80%+ on all 10 tests"
    },
    {
      phase: 3,
      title: "Mastery Phase",
      subtitle: "Hard Simulations 1-10",
      color: "bg-red-500",
      borderColor: "border-red-500",
      textColor: "text-red-600",
      bgColor: "bg-red-50",
      icon: <Trophy className="w-6 h-6" />,
      description: "Tackle the toughest questions to ensure you're over-prepared for any curveball on exam day.",
      tests: hardTests,
      goal: "Score 85%+ on all 10 tests"
    }
  ];

  const benefits = [
    {
      icon: <Rocket className="w-6 h-6" />,
      title: "Progressive Difficulty",
      description: "Start easy and gradually increase difficulty. This scientific approach maximizes retention and builds real confidence."
    },
    {
      icon: <Target className="w-6 h-6" />,
      title: "Structured Learning Path",
      description: "No guessing what to study next. Follow our proven 30-test sequence designed for citizenship test success."
    },
    {
      icon: <Medal className="w-6 h-6" />,
      title: "Clear Milestones",
      description: "Track your progress through each phase. Celebrate completing Easy, Medium, and Hard levels."
    },
    {
      icon: <BarChart3 className="w-6 h-6" />,
      title: "Science-Backed Method",
      description: "Based on spaced repetition and progressive overload principles used in professional exam preparation."
    }
  ];

  return (
    <>
      <SEO
        title="Test Simulations — 30 Timed Practice Tests with Progressive Difficulty"
        description="Practice with 30 Canadian citizenship test simulations across 3 difficulty levels (Easy, Medium, Hard). Each simulation has 20 questions and a 45-minute timer, just like the real IRCC test. 1 free simulation included."
        keywords="Canadian citizenship test simulation, citizenship test practice online, timed citizenship test, citizenship exam simulator, IRCC test practice, mock citizenship test free"
        canonical="https://canadatestprep.ca/features/simulations"
        breadcrumbs={[
          { name: "Home", url: "/" },
          { name: "Features" },
          { name: "Simulations", url: "/features/simulations" }
        ]}
      />
      <div className="min-h-screen bg-white">
      <PublicHeader />

      {/* Hero Section */}
      <section className="bg-gradient-to-b from-slate-50 to-white py-16 lg:py-24">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto">
            <Badge className="bg-truenorth/10 text-truenorth mb-4">Progressive Training System</Badge>
            <h1 className="font-heading font-bold text-4xl sm:text-5xl text-slate-900 leading-tight mb-6">
              Your{" "}
              <span className="text-truenorth">30-Test Journey</span>{" "}
              to Citizenship Success
            </h1>
            <p className="text-lg text-slate-600 mb-8">
              Don't just practice randomly—follow our scientifically designed progression from Easy → Medium → Hard. 
              This structured approach ensures you build a solid foundation before tackling harder questions, 
              maximizing your chances of passing on the first try.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
              <Link to="/register">
                <Button size="lg" className="bg-truenorth hover:bg-truenorth/90 text-white rounded-full px-8" data-testid="hero-cta">
                  Start Your Journey
                  <ChevronRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
            </div>
            <div className="flex items-center justify-center gap-6 text-sm text-slate-600">
              <div className="flex items-center gap-1">
                <Check className="w-4 h-4 text-green-500" />
                <span>30 Progressive Tests</span>
              </div>
              <div className="flex items-center gap-1">
                <Check className="w-4 h-4 text-green-500" />
                <span>3 Difficulty Phases</span>
              </div>
              <div className="flex items-center gap-1">
                <Check className="w-4 h-4 text-green-500" />
                <span>45 mins each</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Visual Journey Map */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="font-heading font-bold text-3xl text-slate-900 mb-4">
              Your Training Roadmap
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Complete each phase in order. Master Easy before Medium, and Medium before Hard. 
              This progressive approach is how top scorers prepare.
            </p>
          </div>

          {/* Journey Timeline */}
          <div className="relative">
            {journeySteps.map((step, stepIndex) => (
              <div key={stepIndex} className="relative mb-12 last:mb-0">
                {/* Connector Line */}
                {stepIndex < journeySteps.length - 1 && (
                  <div className="absolute left-8 top-full w-0.5 h-12 bg-slate-200 hidden md:block" />
                )}
                
                {/* Phase Card */}
                <Card className={`border-2 ${step.borderColor} ${step.bgColor}`} data-testid={`phase-${step.phase}`}>
                  <CardContent className="p-6">
                    {/* Phase Header */}
                    <div className="flex flex-col md:flex-row md:items-center gap-4 mb-6">
                      <div className={`w-16 h-16 ${step.color} rounded-2xl flex items-center justify-center text-white flex-shrink-0`}>
                        {step.icon}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <Badge variant="outline" className={step.textColor}>Phase {step.phase}</Badge>
                          <span className={`text-sm font-medium ${step.textColor}`}>{step.goal}</span>
                        </div>
                        <h3 className="font-heading font-bold text-xl text-slate-900">{step.title}</h3>
                        <p className="text-slate-600">{step.description}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-3xl font-bold text-slate-900">10</p>
                        <p className="text-sm text-slate-500">Tests</p>
                      </div>
                    </div>

                    {/* Test Grid */}
                    <div className="grid grid-cols-5 sm:grid-cols-10 gap-2">
                      {step.tests.map((test, testIndex) => (
                        <div
                          key={testIndex}
                          className={`aspect-square rounded-lg ${step.color} bg-opacity-20 flex items-center justify-center font-bold text-sm ${step.textColor} hover:bg-opacity-40 transition-all cursor-pointer`}
                          title={`${step.subtitle.split(' ')[0]} Test ${test.number}`}
                        >
                          {test.number}
                        </div>
                      ))}
                    </div>

                    {/* Phase Footer */}
                    <div className="mt-4 pt-4 border-t border-slate-200 flex items-center justify-between">
                      <div className="flex items-center gap-2 text-sm text-slate-500">
                        <Timer className="w-4 h-4" />
                        <span>45 minutes per test</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-slate-500">
                        <Target className="w-4 h-4" />
                        <span>20 questions each</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Arrow Between Phases */}
                {stepIndex < journeySteps.length - 1 && (
                  <div className="flex justify-center py-4">
                    <div className="flex flex-col items-center text-slate-400">
                      <ArrowDown className="w-6 h-6" />
                      <span className="text-xs mt-1">Complete to unlock</span>
                    </div>
                  </div>
                )}
              </div>
            ))}

            {/* Final Goal */}
            <div className="mt-8">
              <Card className="border-2 border-maple bg-maple/5">
                <CardContent className="p-6 text-center">
                  <div className="w-16 h-16 bg-maple rounded-full flex items-center justify-center text-white mx-auto mb-4">
                    <Flag className="w-8 h-8" />
                  </div>
                  <h3 className="font-heading font-bold text-xl text-slate-900 mb-2">
                    Ready for the Real Test!
                  </h3>
                  <p className="text-slate-600 mb-4">
                    After completing all 30 simulations, you'll have answered 600 practice questions 
                    and built the confidence to ace your citizenship exam.
                  </p>
                  <Badge className="bg-maple text-white">Trusted by Learners Across Canada</Badge>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Why Progressive Training */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="font-heading font-bold text-3xl text-slate-900 mb-4">
              Why Progressive Training Works
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Random practice doesn't build confidence. Our structured approach is based on 
              proven learning science used by medical and law exam prep programs.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((benefit, index) => (
              <Card key={index} className="bg-white border border-slate-200" data-testid={`benefit-${index}`}>
                <CardContent className="p-6">
                  <div className="w-12 h-12 bg-truenorth/10 rounded-xl flex items-center justify-center text-truenorth mb-4">
                    {benefit.icon}
                  </div>
                  <h3 className="font-semibold text-slate-900 mb-2">{benefit.title}</h3>
                  <p className="text-sm text-slate-600">{benefit.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="font-heading font-bold text-3xl text-slate-900 mb-4">
              How to Use the Training System
            </h2>
          </div>

          <div className="space-y-6">
            <div className="flex gap-4 items-start">
              <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center text-white font-bold flex-shrink-0">1</div>
              <div>
                <h3 className="font-semibold text-slate-900 mb-1">Start with Easy 1</h3>
                <p className="text-slate-600">Begin your journey with the first Easy simulation. Take your time—there's no rush. Focus on understanding each question.</p>
              </div>
            </div>
            <div className="flex gap-4 items-start">
              <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center text-white font-bold flex-shrink-0">2</div>
              <div>
                <h3 className="font-semibold text-slate-900 mb-1">Complete Easy 1-10 in Order</h3>
                <p className="text-slate-600">Work through all 10 Easy simulations sequentially. Aim for 75%+ on each. Review wrong answers before moving on.</p>
              </div>
            </div>
            <div className="flex gap-4 items-start">
              <div className="w-10 h-10 bg-yellow-500 rounded-full flex items-center justify-center text-white font-bold flex-shrink-0">3</div>
              <div>
                <h3 className="font-semibold text-slate-900 mb-1">Progress to Medium</h3>
                <p className="text-slate-600">Once you've mastered Easy, move to Medium simulations. These match the actual test difficulty. Aim for 80%+.</p>
              </div>
            </div>
            <div className="flex gap-4 items-start">
              <div className="w-10 h-10 bg-red-500 rounded-full flex items-center justify-center text-white font-bold flex-shrink-0">4</div>
              <div>
                <h3 className="font-semibold text-slate-900 mb-1">Conquer Hard</h3>
                <p className="text-slate-600">Finally, tackle the Hard simulations. These over-prepare you for the real test. Scoring 85%+ here means you're ready!</p>
              </div>
            </div>
            <div className="flex gap-4 items-start">
              <div className="w-10 h-10 bg-maple rounded-full flex items-center justify-center text-white font-bold flex-shrink-0">5</div>
              <div>
                <h3 className="font-semibold text-slate-900 mb-1">Take Your Official Test with Confidence</h3>
                <p className="text-slate-600">After completing all 30 simulations, you'll walk into your citizenship test knowing you've seen it all. Success is inevitable!</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SEO Content */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4">
          <div className="prose prose-slate max-w-none">
            <h2 className="font-heading font-bold text-2xl text-slate-900 mb-6">
              The Science Behind Progressive Citizenship Test Training
            </h2>
            <p className="text-slate-600 mb-4">
              Random practice is inefficient. Research shows that <strong>progressive difficulty training</strong>—starting easy and gradually increasing challenge—leads to better retention and higher test scores. This is the same methodology used in:
            </p>
            <ul className="list-disc pl-6 text-slate-600 mb-4 space-y-2">
              <li>Medical board exam preparation (USMLE, MCAT)</li>
              <li>Law school exam prep (LSAT, Bar exam)</li>
              <li>Professional certification programs</li>
              <li>Olympic athlete training regimens</li>
            </ul>
            <p className="text-slate-600 mb-4">
              Our 30-simulation system applies these proven principles to Canadian Citizenship Test preparation.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-truenorth">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="font-heading font-bold text-3xl text-white mb-6">
            Begin Your 30-Test Journey Today
          </h2>
          <p className="text-xl text-white/90 mb-8">
            Start with Easy Simulation 1 and work your way to citizenship success. 
            Your future as a Canadian citizen begins with a single test.
          </p>
          <Link to="/register">
            <Button size="lg" className="bg-white text-truenorth hover:bg-slate-100 rounded-full px-10" data-testid="cta-final">
              Start with Easy 1
              <ChevronRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>

      <PublicFooter />
    </div>
    </>
  );
};

export default SimulationsPage;


